import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import './Auth.css';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_new_password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.current_password) {
      newErrors.current_password = 'Current password is required';
    }
    
    if (!formData.new_password) {
      newErrors.new_password = 'New password is required';
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = 'Password must be at least 8 characters long';
    } else if (formData.new_password === formData.current_password) {
      newErrors.new_password = 'New password must be different from current password';
    }
    
    if (!formData.confirm_new_password) {
      newErrors.confirm_new_password = 'Please confirm your new password';
    } else if (formData.new_password !== formData.confirm_new_password) {
      newErrors.confirm_new_password = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        setIsLoading(true);
        setErrors({});

        await authService.changePassword({
          current_password: formData.current_password,
          new_password: formData.new_password,
          confirm_new_password: formData.confirm_new_password
        });

        setSuccess(true);
        
        // Clear form
        setFormData({
          current_password: '',
          new_password: '',
          confirm_new_password: ''
        });

        // Logout user after 3 seconds
        setTimeout(() => {
          logout();
          navigate('/login', { 
            state: { 
              message: 'Password changed successfully. Please login with your new password.' 
            }
          });
        }, 3000);
      } catch (error) {
        setErrors({
          submit: error.message || 'Failed to change password. Please try again.'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-content">
        <div className="auth-form-container">
          <div className="auth-header">
            <h2>Change Password</h2>
            <p>Please enter your current password and choose a new password</p>
          </div>

          {success ? (
            <div className="success-message">
              <i className="fas fa-check-circle"></i>
              <h3>Password Changed Successfully!</h3>
              <p>You will be logged out in a moment. Please login again with your new password.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="current_password">Current Password</label>
                <input
                  type="password"
                  id="current_password"
                  name="current_password"
                  placeholder="Enter your current password"
                  value={formData.current_password}
                  onChange={handleChange}
                  className={errors.current_password ? 'error' : ''}
                />
                {errors.current_password && <span className="error-message">{errors.current_password}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="new_password">New Password</label>
                <input
                  type="password"
                  id="new_password"
                  name="new_password"
                  placeholder="Enter your new password"
                  value={formData.new_password}
                  onChange={handleChange}
                  className={errors.new_password ? 'error' : ''}
                />
                {errors.new_password && <span className="error-message">{errors.new_password}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="confirm_new_password">Confirm New Password</label>
                <input
                  type="password"
                  id="confirm_new_password"
                  name="confirm_new_password"
                  placeholder="Confirm your new password"
                  value={formData.confirm_new_password}
                  onChange={handleChange}
                  className={errors.confirm_new_password ? 'error' : ''}
                />
                {errors.confirm_new_password && <span className="error-message">{errors.confirm_new_password}</span>}
              </div>

              {errors.submit && (
                <div className="error-message submit-error">
                  {errors.submit}
                </div>
              )}

              <button
                type="submit"
                className="auth-button"
                disabled={isLoading}
              >
                {isLoading ? 'Changing Password...' : 'Change Password'}
              </button>
            </form>
          )}
        </div>
        <div className="auth-graphic reset-password">
          {/* Password security illustration from Freepik */}
        </div>
      </div>
    </div>
  );
};

export default ChangePassword; 